import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  addDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { Vessel, MaintenanceRecord, Certification, VesselUsage } from '../../models/types';
import { Reservation } from '../../models/types';
import { db, storage } from '../config';

const COLLECTION_NAME = 'vessels';

// Create a new vessel
export const createVessel = async (vessel: Omit<Vessel, 'id'>): Promise<void> => {
  const vesselsCollection = collection(db, COLLECTION_NAME);
  const docRef = doc(vesselsCollection);
  await setDoc(docRef, {
    ...vessel,
    createdAt: new Date(),
    updatedAt: new Date(),
  });
};

// Get all vessels
export const getVessels = async (): Promise<Vessel[]> => {
  const snapshot = await getDocs(collection(db, COLLECTION_NAME));
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' 
        ? data.createdAt.toDate() 
        : data.createdAt ? new Date(data.createdAt) : undefined,
      updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function'
        ? data.updatedAt.toDate()
        : data.updatedAt ? new Date(data.updatedAt) : undefined,
    } as Vessel;
  });
};

// Get active vessels
export const getActiveVessels = async (): Promise<Vessel[]> => {
  const q = query(collection(db, COLLECTION_NAME), where('isActive', '==', true));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' 
        ? data.createdAt.toDate() 
        : data.createdAt ? new Date(data.createdAt) : undefined,
      updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function'
        ? data.updatedAt.toDate()
        : data.updatedAt ? new Date(data.updatedAt) : undefined,
    } as Vessel;
  });
};

// Get vessel by ID
export const getVesselById = async (id: string): Promise<Vessel | null> => {
  const docRef = doc(db, COLLECTION_NAME, id);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' 
        ? data.createdAt.toDate() 
        : data.createdAt ? new Date(data.createdAt) : undefined,
      updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function'
        ? data.updatedAt.toDate()
        : data.updatedAt ? new Date(data.updatedAt) : undefined,
    } as Vessel;
  }
  
  return null;
};

// Update vessel
export const updateVessel = async (id: string, vessel: Partial<Vessel>): Promise<void> => {
  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, {
    ...vessel,
    updatedAt: new Date(),
  });
};

// Update vessel status with reason
export const updateVesselStatus = async (
  vesselId: string,
  status: 'active' | 'inactive' | 'deleted',
  reason: string
): Promise<void> => {
  try {
    const vesselRef = doc(collection(db, COLLECTION_NAME), vesselId);
    const now = new Date();
    
    await updateDoc(vesselRef, {
      status,
      isActive: status === 'active',
      statusChangeReason: reason,
      statusChangeDate: now,
      updatedAt: now
    });
  } catch (error) {
    console.error('Error updating vessel status:', error);
    throw error;
  }
};

// Delete vessel (sets status to deleted)
export const deleteVessel = async (vesselId: string, reason: string): Promise<void> => {
  try {
    await updateVesselStatus(vesselId, 'deleted', reason);
  } catch (error) {
    console.error('Error deleting vessel:', error);
    throw error;
  }
};

// Set vessel to inactive
export const setVesselInactive = async (vesselId: string, reason: string): Promise<void> => {
  try {
    await updateVesselStatus(vesselId, 'inactive', reason);
  } catch (error) {
    console.error('Error setting vessel inactive:', error);
    throw error;
  }
};

// Upload vessel image
export const uploadVesselImage = async (file: File, vesselId: string): Promise<string> => {
  try {
    const fileExtension = file.name.split('.').pop();
    const fileName = `${vesselId}_${Date.now()}.${fileExtension}`;
    const imageRef = ref(storage, `vessels/${vesselId}/${fileName}`);
    
    await uploadBytes(imageRef, file);
    const downloadURL = await getDownloadURL(imageRef);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading vessel image:', error);
    throw error;
  }
};

/**
 * Gets all vessels grouped by type
 * @returns Object with vessel types as keys and arrays of vessels as values
 */
export const getVesselsByType = async (): Promise<{ [key: string]: Vessel[] }> => {
  try {
    const vesselsQuery = query(collection(db, 'vessels'));
    const vesselsSnapshot = await getDocs(vesselsQuery);
    const vessels = vesselsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Vessel));
    
    // Group vessels by type
    const vesselsByType = vessels.reduce((acc, vessel) => {
      const type = vessel.type || 'Other';
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(vessel);
      return acc;
    }, {} as { [key: string]: Vessel[] });
    
    return vesselsByType;
  } catch (error) {
    console.error('Error getting vessels by type:', error);
    throw error;
  }
};

// Get vessels by propulsion type (powered or non-powered)
export const getVesselsByPropulsionType = async (propulsionType: 'powered' | 'non-powered'): Promise<Vessel[]> => {
  try {
    const q = query(collection(db, COLLECTION_NAME), where('propulsionType', '==', propulsionType));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Vessel[];
  } catch (error) {
    console.error(`Error getting ${propulsionType} vessels:`, error);
    throw error;
  }
};

// Get active vessels by propulsion type
export const getActiveVesselsByPropulsionType = async (propulsionType: 'powered' | 'non-powered'): Promise<Vessel[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME), 
      where('propulsionType', '==', propulsionType),
      where('isActive', '==', true)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Vessel[];
  } catch (error) {
    console.error(`Error getting active ${propulsionType} vessels:`, error);
    throw error;
  }
};

// Get vessels grouped by propulsion type
export const getVesselsByPropulsionTypeGrouped = async (): Promise<{ powered: Vessel[], nonPowered: Vessel[] }> => {
  try {
    const vessels = await getVessels();
    return {
      powered: vessels.filter(vessel => vessel.propulsionType === 'powered'),
      nonPowered: vessels.filter(vessel => vessel.propulsionType === 'non-powered')
    };
  } catch (error) {
    console.error('Error getting vessels by propulsion type:', error);
    throw error;
  }
};

// Get vessels by type and group by propulsion type
export const getVesselsByTypeAndPropulsion = async (): Promise<{ 
  powered: { [key: string]: Vessel[] },
  nonPowered: { [key: string]: Vessel[] }
}> => {
  try {
    const vessels = await getActiveVessels();
    const result = {
      powered: {} as { [key: string]: Vessel[] },
      nonPowered: {} as { [key: string]: Vessel[] }
    };
    
    vessels.forEach(vessel => {
      const propulsionCategory = vessel.propulsionType === 'powered' ? 'powered' : 'nonPowered';
      
      if (!result[propulsionCategory][vessel.type]) {
        result[propulsionCategory][vessel.type] = [];
      }
      
      result[propulsionCategory][vessel.type].push(vessel);
    });
    
    return result;
  } catch (error) {
    console.error('Error getting vessels by type and propulsion:', error);
    throw error;
  }
};

/**
 * Retrieves vessels that are available for assignment on a specific date and time slot
 * @param vesselType The type of vessel to filter by
 * @param date The date to check availability for
 * @param timeSlot Optional time slot to check
 * @param excludeReservationId Optional reservation ID to exclude from availability check
 * @returns Array of available vessels
 */
export const getAvailableVesselsForDate = async (
  vesselType: string,
  date: string,
  timeSlot?: string,
  excludeReservationId?: string
): Promise<Vessel[]> => {
  try {
    // Get all vessels grouped by type
    const vesselsByType = await getVesselsByType();
    
    // Get vessels of the requested type (case-insensitive lookup)
    let vesselsOfType: Vessel[] = [];
    for (const [type, vessels] of Object.entries(vesselsByType)) {
      if (type.toLowerCase() === vesselType.toLowerCase()) {
        vesselsOfType = vessels;
        break;
      }
    }
    
    console.log(`Found ${vesselsOfType.length} vessels of type "${vesselType}" for date ${date}`);
    
    // Get all reservations for the date
    const reservationsQuery = query(
      collection(db, 'reservations'),
      where('date', '==', date),
      where('status', 'in', ['confirmed', 'pending'])
    );
    
    const reservationsSnapshot = await getDocs(reservationsQuery);
    const reservations = reservationsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Reservation));
    
    console.log(`Found ${reservations.length} reservations for date ${date}`);
    
    // Filter out vessels that are already assigned for the given time slot
    const availableVessels = vesselsOfType.filter(vessel => {
      // Skip vessels that are marked as inactive or deleted
      if (vessel.status === 'inactive' || vessel.status === 'deleted' || (vessel.isActive === false)) {
        console.log(`Vessel ${vessel.id} (${vessel.name}) is inactive or deleted`);
        return false;
      }
      
      // Check if the vessel is already assigned to a reservation on this date
      const isAssigned = reservations.some(reservation => {
        // Skip the reservation we're checking in (exclude from availability check)
        if (excludeReservationId && reservation.id === excludeReservationId) {
          console.log(`Excluding reservation ${excludeReservationId} from availability check`);
          return false;
        }
        
        // If the vessel is specifically assigned
        if (reservation.vesselId === vessel.id) {
          // If no time slot is specified, consider it unavailable
          if (!timeSlot) return true;
          
          // Check if the time slots overlap
          const overlaps = reservation.timeSlot === timeSlot;
          if (overlaps) {
            console.log(`Vessel ${vessel.id} is assigned to reservation ${reservation.id} at ${reservation.timeSlot}`);
          }
          return overlaps;
        }
        return false;
      });
      
      if (!isAssigned) {
        console.log(`Vessel ${vessel.id} (${vessel.name}) is available`);
      }
      
      return !isAssigned;
    });
    
    console.log(`${availableVessels.length} vessels are available for assignment`);
    return availableVessels;
  } catch (error) {
    console.error('Error getting available vessels:', error);
    throw error;
  }
};

/**
 * Debug function to check all vessels of a specific type and their status
 */
export const debugVesselsByType = async (vesselType: string): Promise<void> => {
  try {
    console.log(`=== DEBUG: Checking all vessels of type "${vesselType}" ===`);
    
    const vesselsByType = await getVesselsByType();
    const allVessels = vesselsByType[vesselType] || [];
    
    console.log(`Total vessels found for type "${vesselType}": ${allVessels.length}`);
    
    allVessels.forEach((vessel, index) => {
      console.log(`Vessel ${index + 1}:`, {
        id: vessel.id,
        name: vessel.name,
        registrationNumber: vessel.registrationNumber,
        type: vessel.type,
        status: vessel.status,
        isActive: vessel.isActive,
        capacity: vessel.capacity,
        propulsionType: vessel.propulsionType
      });
    });
    
    // Also check case-insensitive matches
    let caseInsensitiveMatches: Vessel[] = [];
    for (const [type, vessels] of Object.entries(vesselsByType)) {
      if (type.toLowerCase() === vesselType.toLowerCase()) {
        caseInsensitiveMatches = vessels;
        console.log(`Case-insensitive match found for type "${type}"`);
        break;
      }
    }
    
    const activeVessels = allVessels.filter(vessel => 
      vessel.status === 'active' && vessel.isActive === true
    );
    
    console.log(`Active vessels: ${activeVessels.length}/${allVessels.length}`);
    
    if (activeVessels.length < allVessels.length) {
      console.log('Inactive vessels:');
      allVessels.filter(vessel => 
        vessel.status !== 'active' || vessel.isActive !== true
      ).forEach(vessel => {
        console.log(`  - ${vessel.name} (${vessel.id}): status="${vessel.status}", isActive=${vessel.isActive}`);
      });
    }
    
    console.log('=== END DEBUG ===');
  } catch (error) {
    console.error('Error in debugVesselsByType:', error);
  }
};

// Enhanced vessel management functions

/**
 * Enhanced update vessel status with additional statuses
 */
export const updateVesselStatusEnhanced = async (
  vesselId: string,
  status: 'active' | 'inactive' | 'maintenance' | 'out-of-service' | 'deleted',
  reason: string
): Promise<void> => {
  try {
    const vesselRef = doc(collection(db, COLLECTION_NAME), vesselId);
    const now = new Date();
    
    await updateDoc(vesselRef, {
      status,
      isActive: status === 'active',
      statusChangeReason: reason,
      statusChangeDate: now,
      updatedAt: now
    });
  } catch (error) {
    console.error('Error updating vessel status:', error);
    throw error;
  }
};

/**
 * Add a maintenance record to a vessel
 */
export const addMaintenanceRecord = async (vesselId: string, maintenanceRecord: Omit<MaintenanceRecord, 'id'>): Promise<void> => {
  try {
    const vessel = await getVesselById(vesselId);
    if (!vessel) throw new Error('Vessel not found');

    const newRecord: MaintenanceRecord = {
      ...maintenanceRecord,
      id: uuidv4()
    };

    const updatedRecords = [...(vessel.maintenanceRecords || []), newRecord];
    
    // Update the vessel with the new maintenance record
    await updateVessel(vesselId, {
      maintenanceRecords: updatedRecords,
      lastMaintenanceDate: new Date(newRecord.date),
      totalMaintenanceCost: (vessel.totalMaintenanceCost || 0) + (newRecord.cost || 0)
    });
  } catch (error) {
    console.error('Error adding maintenance record:', error);
    throw error;
  }
};

/**
 * Update a maintenance record
 */
export const updateMaintenanceRecord = async (vesselId: string, recordId: string, updates: Partial<MaintenanceRecord>): Promise<void> => {
  try {
    const vessel = await getVesselById(vesselId);
    if (!vessel) throw new Error('Vessel not found');

    const updatedRecords = vessel.maintenanceRecords?.map(record => 
      record.id === recordId ? { ...record, ...updates } : record
    ) || [];

    await updateVessel(vesselId, {
      maintenanceRecords: updatedRecords
    });
  } catch (error) {
    console.error('Error updating maintenance record:', error);
    throw error;
  }
};

/**
 * Delete a maintenance record
 */
export const deleteMaintenanceRecord = async (vesselId: string, recordId: string): Promise<void> => {
  try {
    const vessel = await getVesselById(vesselId);
    if (!vessel) throw new Error('Vessel not found');

    const updatedRecords = vessel.maintenanceRecords?.filter(record => record.id !== recordId) || [];

    await updateVessel(vesselId, {
      maintenanceRecords: updatedRecords
    });
  } catch (error) {
    console.error('Error deleting maintenance record:', error);
    throw error;
  }
};

/**
 * Add a certification to a vessel
 */
export const addCertification = async (vesselId: string, certification: Omit<Certification, 'id'>): Promise<void> => {
  try {
    const vessel = await getVesselById(vesselId);
    if (!vessel) throw new Error('Vessel not found');

    const newCertification: Certification = {
      ...certification,
      id: uuidv4()
    };

    const updatedCertifications = [...(vessel.certifications || []), newCertification];

    await updateVessel(vesselId, {
      certifications: updatedCertifications
    });
  } catch (error) {
    console.error('Error adding certification:', error);
    throw error;
  }
};

/**
 * Update a certification
 */
export const updateCertification = async (vesselId: string, certificationId: string, updates: Partial<Certification>): Promise<void> => {
  try {
    const vessel = await getVesselById(vesselId);
    if (!vessel) throw new Error('Vessel not found');

    const updatedCertifications = vessel.certifications?.map(cert => 
      cert.id === certificationId ? { ...cert, ...updates } : cert
    ) || [];

    await updateVessel(vesselId, {
      certifications: updatedCertifications
    });
  } catch (error) {
    console.error('Error updating certification:', error);
    throw error;
  }
};

/**
 * Delete a certification
 */
export const deleteCertification = async (vesselId: string, certificationId: string): Promise<void> => {
  try {
    const vessel = await getVesselById(vesselId);
    if (!vessel) throw new Error('Vessel not found');

    const updatedCertifications = vessel.certifications?.filter(cert => cert.id !== certificationId) || [];

    await updateVessel(vesselId, {
      certifications: updatedCertifications
    });
  } catch (error) {
    console.error('Error deleting certification:', error);
    throw error;
  }
};

/**
 * Update vessel usage statistics
 */
export const updateVesselUsage = async (vesselId: string, usageUpdate: Partial<VesselUsage>): Promise<void> => {
  try {
    const vessel = await getVesselById(vesselId);
    if (!vessel) throw new Error('Vessel not found');

    const currentUsage = vessel.usage || {};
    const updatedUsage = { ...currentUsage, ...usageUpdate };

    await updateVessel(vesselId, {
      usage: updatedUsage
    });
  } catch (error) {
    console.error('Error updating vessel usage:', error);
    throw error;
  }
};

/**
 * Get vessels that need maintenance attention
 */
export const getVesselsNeedingMaintenance = async (): Promise<Vessel[]> => {
  try {
    const vessels = await getActiveVessels();
    const now = new Date();
    const oneMonthFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    return vessels.filter(vessel => {
      // Check if maintenance is due
      if (vessel.nextMaintenanceDue && new Date(vessel.nextMaintenanceDue) <= oneMonthFromNow) {
        return true;
      }
      return false;
    });
  } catch (error) {
    console.error('Error getting vessels needing maintenance:', error);
    throw error;
  }
};

/**
 * Get vessels with expiring certifications
 */
export const getVesselsWithExpiringCertifications = async (): Promise<Vessel[]> => {
  try {
    const vessels = await getActiveVessels();
    const now = new Date();
    const oneMonthFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    return vessels.filter(vessel => {
      if (vessel.certifications) {
        return vessel.certifications.some(cert => 
          new Date(cert.expiryDate) <= oneMonthFromNow
        );
      }
      return false;
    });
  } catch (error) {
    console.error('Error getting vessels with expiring certifications:', error);
    throw error;
  }
};

/**
 * Get vessel maintenance history
 */
export const getVesselMaintenanceHistory = async (vesselId: string): Promise<MaintenanceRecord[]> => {
  try {
    const vessel = await getVesselById(vesselId);
    if (!vessel) throw new Error('Vessel not found');
    
    return vessel.maintenanceRecords || [];
  } catch (error) {
    console.error('Error getting vessel maintenance history:', error);
    throw error;
  }
};

/**
 * Get vessel certifications
 */
export const getVesselCertifications = async (vesselId: string): Promise<Certification[]> => {
  try {
    const vessel = await getVesselById(vesselId);
    if (!vessel) throw new Error('Vessel not found');
    
    return vessel.certifications || [];
  } catch (error) {
    console.error('Error getting vessel certifications:', error);
    throw error;
  }
}; 

/**
 * Quick fix function to reactivate a vessel
 */
export const reactivateVessel = async (vesselId: string): Promise<void> => {
  try {
    console.log(`Reactivating vessel: ${vesselId}`);
    const vesselRef = doc(collection(db, COLLECTION_NAME), vesselId);
    
    await updateDoc(vesselRef, {
      status: 'active',
      isActive: true,
      statusChangeReason: 'Reactivated via debug function',
      statusChangeDate: new Date(),
      updatedAt: new Date()
    });
    
    console.log(`Vessel ${vesselId} has been reactivated`);
  } catch (error) {
    console.error('Error reactivating vessel:', error);
    throw error;
  }
}; 